/* Mangomint — a `scheduling` plugin.
 *
 * Everything that knows Mangomint exists lives in this file: its tables, its two
 * different ideas of what a timestamp means, and the shape of the service map.
 * The store gets normalised appointments and nothing else, so a second booking
 * system is another file like this one rather than a change to the board.
 *
 * Data arrives here by way of the Mangomint webhooks that fill `public.*` in
 * Supabase; this reads those tables, and never writes to them. The only things
 * it writes are `tt_service_map` and `tt_option_rules`, which are ours.
 */
import { plugins } from '../plugins.js';
import { sync } from '../sync.js';
import { tenant } from '../tenant.js';

var sb = sync.sb;

function ms(v) { return v ? new Date(v).getTime() : null; }

/* ---------------- the two timestamp conventions ---------------- */

// Mangomint writes salon wall-clock time but tags it +00, so a 10:00 AM
// appointment is stored as 10:00Z rather than 15:00Z. Every appointment in the
// table falls between 10:10 and 17:30 "UTC", which is a salon day, not a
// 5 AM–12:30 PM one. So the stored day boundary is UTC midnight, and the
// instant has to be rebuilt from the UTC clock parts before it means anything.
function dayRange(dateKey) {
  var p = dateKey.split('-');
  var start = Date.UTC(Number(p[0]), Number(p[1]) - 1, Number(p[2]));
  return {
    start: new Date(start).toISOString(),
    end: new Date(start + 86400000).toISOString()
  };
}

function apptInstant(iso) {
  if (!iso) return null;
  var d = new Date(iso);
  if (isNaN(d.getTime())) return null;
  return new Date(
    d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(),
    d.getUTCHours(), d.getUTCMinutes(), d.getUTCSeconds()
  ).getTime();
}

/* ---------------- the book ---------------- */

// One string on purpose: PostgREST select syntax is comma-delimited, so an
// array joined with ', ' doubles up the commas the lines already carry.
var APPT_SELECT =
  'id,date_time,notes,is_first_appointment,is_deleted,cancellation_type,client_id,' +
  // `notes` is the client's file, read in by the server poll (schema part 20).
  'client:clients(id,first_name,last_name,notes),' +
  'appointment_services(' +
    'id,date_time,duration_minutes,price,service_id,' +
    'service:services(id,name),' +
    'appointment_service_staff(staff:staff(id,first_name,last_name)),' +
    'appointment_service_option_selections(' +
      'option_group_id,option_group_name,selected_option_id,selected_option_name)' +
  ')';

async function fetchAppointments(dateKey) {
  var range = dayRange(dateKey);
  var res = await sb
    .from('appointments')
    .select(APPT_SELECT)
    .gte('date_time', range.start)
    .lt('date_time', range.end)
    .order('date_time');
  if (res.error) throw new Error(res.error.message);

  return (res.data || []).map(function (a) {
    var client = a.client || {};
    var services = (a.appointment_services || []).map(function (s) {
      var staffRows = (s.appointment_service_staff || [])
        .map(function (x) { return x.staff; })
        .filter(Boolean);
      var techs = staffRows
        .map(function (t) {
          return (t.first_name || '') + (t.last_name ? ' ' + t.last_name.charAt(0) + '.' : '');
        });
      // Only selections the system identified. A row without an option id is
      // from an older ingest that stored the names joined into one string;
      // there is nothing there to key a rule on.
      var options = (s.appointment_service_option_selections || [])
        .filter(function (o) { return o.selected_option_id != null; })
        .map(function (o) {
          return {
            id: o.selected_option_id,
            name: o.selected_option_name || 'Option',
            groupId: o.option_group_id,
            groupName: o.option_group_name || ''
          };
        });
      return {
        id: s.id,
        appointmentId: a.id,
        serviceId: s.service_id,
        name: (s.service && s.service.name) || 'Service',
        startsAt: apptInstant(s.date_time) || apptInstant(a.date_time),
        durationMinutes: s.duration_minutes || 0,
        // With its options in it; null when the mirror has none (part 18).
        price: s.price == null ? null : Number(s.price),
        techs: techs,
        // The same people by Mangomint's id, as strings, to set against a
        // technician's `external_staff_id`. A name can't be sent to the proxy.
        techIds: staffRows.map(function (t) { return String(t.id); }),
        options: options
      };
    }).sort(function (x, y) { return x.startsAt - y.startsAt; });

    // The id is who this is; the name is only what to draw. A walk-in is
    // named later, sometimes hours later, and two of them on the same day are
    // two different people however alike their names read.
    return {
      id: a.id,
      clientId: a.client_id != null ? a.client_id : (client.id != null ? client.id : null),
      startsAt: apptInstant(a.date_time),
      notes: a.notes || '',
      isFirst: !!a.is_first_appointment,
      cancelled: !!a.is_deleted || !!a.cancellation_type,
      clientName: [client.first_name, client.last_name].filter(Boolean).join(' ') || 'Walk-in',
      // The notes on the client's file — not `notes` above, which is this
      // appointment's. Blank for none.
      clientNotes: client.notes ? String(client.notes).trim() : '',
      services: services
    };
  }).sort(function (x, y) { return x.startsAt - y.startsAt; });
}

/* ---------------- checkout ---------------- */

// Checking a client out closes their ticket, which is the real end of the
// service. Returns { mangomintAppointmentId: closedAtMs }.
//
// sales.closed_at is a GENUINE UTC INSTANT — the opposite of the wall-clock
// fiction in appointments.date_time above, so it must not go through
// apptInstant(). Checked against 30 days of tickets: closes run 14:00–01:00
// UTC (9 AM–8 PM Central) with nothing at all between 04:00 and 13:00. Push it
// through apptInstant() and every turn stops five hours late.
//
// sale_date is the salon's business date, so it is the right filter: a ticket
// closed at 7:30 PM Central lands on the next UTC day but the same sale_date.
async function fetchClosedTickets(dateKey) {
  var res = await sb
    .from('sales')
    .select('id,appointment_ids,closed_at,reopened_at,voided_at,deleted_at')
    .eq('sale_date', dateKey)
    .not('closed_at', 'is', null);
  if (res.error) throw new Error(res.error.message);

  var out = {};
  (res.data || []).forEach(function (s) {
    var closed = settledAt(s);
    if (!closed) return;
    // One sale can settle several appointments (a shared ticket), and one
    // appointment can be split across sales — the last close wins so a split
    // ticket never understates how long the service ran.
    (s.appointment_ids || []).forEach(function (id) {
      if (!out[id] || closed > out[id]) out[id] = closed;
    });
  });
  return out;
}

// When a sale settled, or null if it has not: voided, deleted, never closed,
// or reopened since its close — which is a ticket open again, not settled.
function settledAt(s) {
  if (s.voided_at || s.deleted_at) return null;
  var closed = ms(s.closed_at);
  if (!closed) return null;
  if (s.reopened_at && ms(s.reopened_at) > closed) return null;
  return closed;
}

/* Who the sale has on each booked service — which is who the commission goes
 * to, and so who the board writes a finished service up on. Returns
 * { mangomintAppointmentId: [{ serviceId, staffId, label }] } for settled sales
 * only: a ticket still open, or reopened, is not yet the record of anything.
 *
 * `staffId` is Mangomint's, as a string, to set against a technician's
 * `external_staff_id`. `label` is the same "First L." the book's tech names
 * are, for a technician whose id nobody has filled in. Lines with nobody on
 * them are kept with a null staff, so a line can be counted even when it
 * cannot be written up. */
async function fetchSaleStaff(dateKey) {
  var res = await sb
    .from('sales')
    .select('id,closed_at,reopened_at,voided_at,deleted_at,' +
            'sale_lines(appointment_id,service_id,staff_id)')
    .eq('sale_date', dateKey)
    .not('closed_at', 'is', null);
  if (res.error) throw new Error(res.error.message);

  var lines = [];
  (res.data || []).forEach(function (s) {
    if (!settledAt(s)) return;
    (s.sale_lines || []).forEach(function (l) { lines.push(l); });
  });

  var ids = [];
  lines.forEach(function (l) {
    if (l.staff_id != null && ids.indexOf(l.staff_id) === -1) ids.push(l.staff_id);
  });
  var names = {};
  if (ids.length) {
    var who = await sb.from('staff').select('id,first_name,last_name').in('id', ids);
    if (who.error) throw new Error(who.error.message);
    (who.data || []).forEach(function (t) {
      names[t.id] = (t.first_name || '') +
        (t.last_name ? ' ' + t.last_name.charAt(0) + '.' : '');
    });
  }

  var out = {};
  lines.forEach(function (l) {
    (out[l.appointment_id] = out[l.appointment_id] || []).push({
      serviceId: l.service_id,
      staffId: l.staff_id == null ? null : String(l.staff_id),
      label: l.staff_id == null ? '' : names[l.staff_id] || ''
    });
  });
  return out;
}

/* ---------------- requests ----------------
 * Who Mangomint has each booked service requested of. Its webhooks never say,
 * so the server's `requests_sync` poll reads the calendar once a minute and
 * keeps `appointment_requests` (schema part 12); this only reads that table.
 *
 * Keyed as the book's own lines can be found again — appointment, service and
 * start — because a line id does not survive an edit. `startsAt` goes through
 * apptInstant() for the same reason the book's does: it is the mirror's
 * wall-clock-as-UTC, so the two compare equal. Staff ids are Mangomint's, as
 * strings, to set against a technician's `external_staff_id`.
 *
 * `type` is Mangomint's own word: `SpecificStaff` is a request, `Anyone` is
 * Mangomint saying it is not one. A service with no row is one the poll has not
 * reported on, which is not the same thing. */
async function fetchBookedRequests(dateKey) {
  var res = await sb
    .from('appointment_requests')
    .select('appointment_id,service_id,starts_at,staff_id,staff_requested_type,' +
            'additional_staff_id,additional_staff_requested_type')
    .eq('business_date', dateKey);
  if (res.error) throw new Error(res.error.message);
  return (res.data || []).map(function (r) {
    return {
      appointmentId: r.appointment_id,
      serviceId: r.service_id,
      startsAt: apptInstant(r.starts_at),
      staffId: r.staff_id == null ? null : String(r.staff_id),
      type: r.staff_requested_type || null,
      additionalStaffId: r.additional_staff_id == null ? null : String(r.additional_staff_id),
      additionalType: r.additional_staff_requested_type || null
    };
  });
}

/* ---------------- who is working, and when ----------------
 * Each provider's shift today and the time blocked off on Mangomint's calendar
 * (lunch, a break, "late"), kept by the server's minute poll in `staff_shifts`
 * and `staff_time_blocks` (schema part 17) — the webhooks carry neither. Both
 * are in the mirror's wall-clock convention, so they go through apptInstant().
 * `changed_at` is a genuine instant: when the poll saw that provider's hours
 * change, which is what decides whether a time typed at the desk still stands.
 *
 * Returns { mangomintStaffId: { spans: [{ from, to }], blocks: [{ id, from, to,
 * label }], changedAt } }, keyed by Mangomint's id as a string. */
async function fetchWorkHours(dateKey) {
  var both = await Promise.all([
    sb.from('staff_shifts').select('staff_id,starts_at,ends_at,changed_at')
      .eq('business_date', dateKey),
    sb.from('staff_time_blocks').select('block_id,staff_id,starts_at,ends_at,description')
      .eq('business_date', dateKey)
  ]);
  if (both[0].error) throw new Error(both[0].error.message);
  if (both[1].error) throw new Error(both[1].error.message);
  var out = {};
  function entry(id) {
    var k = String(id);
    return out[k] || (out[k] = { spans: [], blocks: [], changedAt: null });
  }
  (both[0].data || []).forEach(function (r) {
    var e = entry(r.staff_id);
    e.spans.push({ from: apptInstant(r.starts_at), to: apptInstant(r.ends_at) });
    var c = ms(r.changed_at);
    if (c != null && (e.changedAt == null || c > e.changedAt)) e.changedAt = c;
  });
  // A block on somebody Mangomint does not have working is no use to anybody:
  // the board only acts on a day it has a shift for.
  (both[1].data || []).forEach(function (r) {
    if (!out[String(r.staff_id)]) return;
    out[String(r.staff_id)].blocks.push({
      id: r.block_id, from: apptInstant(r.starts_at), to: apptInstant(r.ends_at),
      label: r.description || 'Break'
    });
  });
  return out;
}

/* ---------------- the waiting room ----------------
 * Each of the day's appointments as Mangomint has it: Unconfirmed, Confirmed,
 * Waiting, CheckedIn, Finished or NoShow, and when it was checked in. Kept by
 * the server's minute poll in `appointment_status` (schema part 15) — the
 * webhooks carry none of it. Returns { appointmentId: { status, waitingSince,
 * checkedInAt } } with the times as ms; both are genuine instants. */
async function fetchBookStatus(dateKey) {
  var res = await sb
    .from('appointment_status')
    .select('appointment_id,workflow_status,waiting_since,checked_in_at,services_added')
    .eq('business_date', dateKey);
  if (res.error) throw new Error(res.error.message);
  var out = {};
  (res.data || []).forEach(function (r) {
    out[r.appointment_id] = {
      status: r.workflow_status || null,
      waitingSince: r.waiting_since ? Date.parse(r.waiting_since) : null,
      checkedInAt: r.checked_in_at ? Date.parse(r.checked_in_at) : null,
      // Every service added to the visit and when, from its history (part 16):
      // what tells an upsell at the chair from a visit booked that way.
      servicesAdded: (Array.isArray(r.services_added) ? r.services_added : []).map(function (x) {
        return { serviceId: x.service_id, at: Date.parse(x.at) };
      })
    };
  });
  return out;
}

/* ---------------- service catalogue + mapping ----------------
 * tt_service_map is keyed on `mangomint_service_id`. That column name is this
 * plugin's business: a second provider gets its own storage rather than
 * borrowing this table, which is why every read and write of it is in here.
 */

async function fetchCatalogue() {
  var results = await Promise.all([
    sb.from('services').select('id, name').order('name'),
    sb.from('tt_service_map').select('*')
  ]);
  var bad = results.find(function (r) { return r.error; });
  if (bad) throw new Error(bad.error.message);
  var map = {};
  (results[1].data || []).forEach(function (r) {
    map[r.mangomint_service_id] = r.tt_service_id;
  });
  return { catalogue: results[0].data || [], map: map };
}

async function fetchServiceMap() {
  var res = await sb.from('tt_service_map').select('*');
  if (res.error) throw new Error(res.error.message);
  var map = {};
  (res.data || []).forEach(function (r) { map[r.mangomint_service_id] = r.tt_service_id; });
  return map;
}

// Through the write queue like every other mutation, so a mapping set while
// the wifi is down is not lost.
function saveServiceMapping(serviceId, ttServiceId) {
  if (ttServiceId) {
    sync.enqueue({
      table: 'tt_service_map', type: 'upsert',
      row: {
        mangomint_service_id: serviceId,
        tt_service_id: ttServiceId,
        updated_at: new Date().toISOString()
      },
      onConflict: 'tenant_id,mangomint_service_id'
    });
  } else {
    sync.enqueue({
      table: 'tt_service_map', type: 'delete',
      match: { mangomint_service_id: serviceId }
    });
  }
}

// One statement for the lot rather than a round trip per mapping.
function clearServiceMap(serviceIds) {
  if (!serviceIds || !serviceIds.length) return;
  sync.enqueue({
    table: 'tt_service_map', type: 'delete',
    matchIn: { mangomint_service_id: serviceIds.slice() }
  });
}

/* ---------------- service options + their rules ----------------
 * The options a booking can carry are Mangomint's; what they do to a turn is
 * ours, in tt_option_rules. Keyed on the option id rather than its name: the
 * same word appears under several groups ("Gel" under Pedicure Options is a
 * different option from "Gel" under Manicure Addons) and a rule that matched
 * on the word would fire on the wrong services.
 */

// Every option the salon offers, grouped, for the mapping screen. Ordered so
// the list reads the way the booking screen does.
/* `baseServiceIds` is every turn type the option can be booked on — the turn
 * types of the services offering its group (the daily catalogue sync keeps
 * `service_option_group_services`), through the service map. Empty when that
 * is not known, which leaves the rules screen with its one plain answer. */
async function fetchOptionCatalogue() {
  var results = await Promise.all([
    sb.from('service_option_groups').select('id, name').order('name'),
    sb.from('service_option_group_options').select('id, option_group_id, name').order('name'),
    sb.from('service_option_group_services').select('service_id, option_group_id'),
    sb.from('tt_service_map').select('mangomint_service_id, tt_service_id')
  ]);
  var bad = results.find(function (r) { return r.error; });
  if (bad) throw new Error(bad.error.message);
  var groups = {};
  (results[0].data || []).forEach(function (g) { groups[g.id] = g.name; });
  var typeOf = {};
  (results[3].data || []).forEach(function (m) { typeOf[m.mangomint_service_id] = m.tt_service_id; });
  var basesByGroup = {};
  (results[2].data || []).forEach(function (l) {
    var t = typeOf[l.service_id];
    if (!t) return;
    var list = basesByGroup[l.option_group_id] = basesByGroup[l.option_group_id] || [];
    if (list.indexOf(t) === -1) list.push(t);
  });
  return (results[1].data || []).map(function (o) {
    return {
      id: o.id,
      name: o.name,
      groupId: o.option_group_id,
      groupName: groups[o.option_group_id] || 'Other options',
      baseServiceIds: basesByGroup[o.option_group_id] || []
    };
  });
}

async function fetchOptionRules() {
  var res = await sb.from('tt_option_rules').select('*');
  if (res.error) throw new Error(res.error.message);
  var map = {};
  (res.data || []).forEach(function (r) {
    map[r.option_id] = {
      optionId: r.option_id,
      optionName: r.option_name || '',
      groupId: r.group_id,
      groupName: r.group_name || '',
      serviceId: r.tt_service_id || null,
      // What it makes a booking per turn type the booking was booked as;
      // `serviceId` covers the rest. See js/rules/option-type.js.
      byBase: r.by_base || {},
      staffIds: r.staff_ids || []
    };
  });
  return map;
}

// A rule with nothing in it is not a rule — it is deleted rather than stored
// as a row that says "this option does nothing", which is the default anyway.
function saveOptionRule(option, rule) {
  var byBase = (rule && rule.byBase) || {};
  var empty = !rule || (!rule.serviceId && !(rule.staffIds || []).length &&
                        !Object.keys(byBase).length);
  if (empty) {
    sync.enqueue({
      table: 'tt_option_rules', type: 'delete', match: { option_id: option.id }
    });
    return;
  }
  sync.enqueue({
    table: 'tt_option_rules', type: 'upsert',
    row: {
      option_id: option.id,
      option_name: option.name,
      group_id: option.groupId,
      group_name: option.groupName,
      tt_service_id: rule.serviceId || null,
      by_base: Object.assign({}, byBase),
      staff_ids: (rule.staffIds || []).slice(),
      updated_at: new Date().toISOString()
    },
    onConflict: 'tenant_id,option_id'
  });
}

function clearOptionRules(optionIds) {
  if (!optionIds || !optionIds.length) return;
  sync.enqueue({
    table: 'tt_option_rules', type: 'delete', matchIn: { option_id: optionIds.slice() }
  });
}

/* ---------------- realtime ---------------- */

// `sales` rides along on the same channel. The table names stop here: the
// store is told 'book' or 'checkout', so a checkout only refreshes tickets
// rather than reloading the whole book and its client join.
//
// Filtered by tenant for the same reason the board's channel is — every salon's
// booking write would otherwise wake every other salon's appointments pane.
// These five are on REPLICA IDENTITY FULL, which is what makes the filter
// correct on deletes too; see server/schema/04-mirror-tables.sql.
// `onJoined` as in sync.subscribe: a rejoin means book events were missed.
function subscribe(onChange, onJoined) {
  var id = tenant.get();
  var channel = sb.channel(id ? 'tt-appointments:' + id : 'tt-appointments');
  var TABLES = {
    appointments: 'book',
    appointment_services: 'book',
    appointment_service_staff: 'book',
    sales: 'checkout',
    sale_lines: 'checkout',
    appointment_requests: 'requests',
    appointment_status: 'status',
    staff_shifts: 'shifts',
    staff_time_blocks: 'shifts',
    // A client's notes, filled in by the server poll after the book has loaded.
    clients: 'book'
  };
  Object.keys(TABLES).forEach(function (table) {
    var binding = { event: '*', schema: 'public', table: table };
    if (id) binding.filter = 'tenant_id=eq.' + id;
    channel.on('postgres_changes', binding, function () {
      onChange(TABLES[table]);
    });
  });
  channel.subscribe(function (state) {
    if (state === 'SUBSCRIBED' && onJoined) onJoined();
  });
  return channel;
}

function unsubscribe(handle) {
  if (handle) sync.removeChannel(handle);
}

plugins.register({
  id: 'mangomint',
  kind: 'scheduling',
  name: 'Mangomint',
  blurb: 'Today’s book, the service catalogue and checkout times, read live from ' +
         'the Mangomint data in Supabase. Read-only — nothing is written back.',
  // No `inService`. Mangomint's webhooks carry no appointment status of any
  // kind — not on the appointment, not on its services, not as an event type
  // — checked against every payload it has sent us; and the ticket it opens
  // when the desk hits Start is not published until the sale completes, so
  // `sales` never holds an open one either. Until a status arrives, the board
  // works the moment out for itself; see the auto-start section in store.js.
  capabilities: { catalogue: true, checkout: true, realtime: true, options: true,
                  saleStaff: true, requests: true, waitingRoom: true, workHours: true },
  provider: {
    needsAuth: true,
    fetchAppointments: fetchAppointments,
    fetchClosedTickets: fetchClosedTickets,
    fetchSaleStaff: fetchSaleStaff,
    fetchBookedRequests: fetchBookedRequests,
    fetchBookStatus: fetchBookStatus,
    fetchWorkHours: fetchWorkHours,
    fetchCatalogue: fetchCatalogue,
    fetchServiceMap: fetchServiceMap,
    fetchOptionCatalogue: fetchOptionCatalogue,
    fetchOptionRules: fetchOptionRules,
    saveOptionRule: saveOptionRule,
    clearOptionRules: clearOptionRules,
    saveServiceMapping: saveServiceMapping,
    clearServiceMap: clearServiceMap,
    subscribe: subscribe,
    unsubscribe: unsubscribe
  }
});
